import { useEffect } from 'react';


// Age calculator
export function calculateAge(dob) {

  if (!dob) return null;

  const birthday = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthday.getFullYear();
  const m = today.getMonth() - birthday.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
    age--;
  }
  return age;
}

// Formats date to "YYYY-MM-DDT00:00:00"
export const formatDateForAPI = (date) => {
  if (!date) return null; // Return null if the date is not available
  // Adjust for the timezone offset to avoid off-by-one errors
  const userTimezoneOffset = date.getTimezoneOffset() * 60000; // Offset in milliseconds
  const adjustedDate = new Date(date.getTime() - userTimezoneOffset);
  return `${adjustedDate.toISOString().split('T')[0]}T00:00:00`;
}


export function formatDateAsUTC(dateString, language) {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("T")[0].split("-");
  const date = new Date(Date.UTC(year, month - 1, day));

  // Map "English" to "en-US" and "Français" to "fr-FR"
  const locale = language === "Français" ? "fr-FR" : "en-US";

  // Function to determine the ordinal suffix (only for English)
  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return "th"; // Special case for 11th, 12th, 13th
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  // Format the date
  let formattedDate = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC"
  }).format(date);

  // Apply ordinal suffix **only for English**
  if (language !== "Français") {
    formattedDate = formattedDate.replace(/\b\d+\b/, (match) => `${match}${getOrdinalSuffix(parseInt(match, 10))}`);
  }

  return formattedDate;
}

export function formatYearMonthAsUTC(dateString, language) {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("T")[0].split("-");
  const date = new Date(Date.UTC(year, month - 1, day));

  const locale = language === "Français" ? "fr-FR" : "en-US";

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    timeZone: "UTC"
  }).format(date);
}

export function parseYearMonthStringToValidDate(ym) {
  const s = (ym || "").trim();
  const m = /^(\d{4})-(\d{2})$/.exec(s);
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]); // 1..12
  if (year < 1000 || year > 9999) return null;
  if (month < 1 || month > 12) return null;

  // Use midday to avoid DST edge cases
  return new Date(year, month - 1, 1, 12, 0, 0, 0);
}

export const parseDateStringToValidDate = (str) => {
  if (!str) return null;

  const parts = str.split('-');

  if (parts.length === 2 && parts[0].length === 4 && parts[1].length === 2) {
    // YYYY-MM → set first day
    return new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
  }

  if (parts.length === 3) {
    const [yyyy, mm, dd] = parts;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
};

export const formatPhoneNumber = (phoneNumber) => {
  // Remove any non-numeric characters from the phone number
  const digits = phoneNumber.replace(/\D/g, '');

  if (digits.length === 0) return ''; // Return empty string for no input

  // Handle different cases based on the length of digits
  if (digits.length <= 3) {
    return `(${digits}`;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
};

// Functions used in height component
// Function to convert centimeters to feet and inches
export function convertCmToFeetInches(cm) {
  const inchesTotal = cm / 2.54;
  let feet = Math.floor(inchesTotal / 12);
  let inches = Math.round(inchesTotal % 12);

  // If inches round up to 12, adjust feet and reset inches
  if (inches === 12) {
    feet += 1;
    inches = 0;
  }

  return { feet, inches };
}
// Function to convert feet and inches to centimeters
export function convertHeightToCm(feet, inches) {
  return Math.round((feet * 30.48) + (inches * 2.54));
}

// Weight calculator
// Functions to convert kilograms to pounds and vice versa used in Weight component
export function convertKgToLbs(kg) {
  return Math.round(kg * 2.20462); // 1 kg = 2.20462 lbs
}
export function convertLbsToKg(lbs) {
  return Math.round(lbs / 2.20462); // 1 lb = 0.453592 kg
}

export const formatDateAndAge = (birthdate) => {
  const birthDate = new Date(birthdate);
  if (isNaN(birthDate.getTime())) {
    return null;
  }

  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  const formattedDate = birthDate.toLocaleDateString('en-US', options);
  const age = calculateAge(birthdate);

  return `${formattedDate} - ${age} Years`;
}

// SIN validation - Not used in the project anymore
/*
function luhn(str) {
  str = str.replace(/[^0-9]/g, ''); // Remove non-numeric characters
  let odd = !str.length % 2;
  let sum = 0;

  for (let i = 0; i < str.length; ++i) {
    let n = parseInt(str[i], 10); // Explicitly parse the character as a number
    odd = !odd;
    if (odd) {
      sum += n;

    } else {
      let x = 2 * n;
      sum += x > 9 ? x - 9 : x;
    }
  }
  return (sum % 10) === 0;
}

export function validateSIN(sin) {
  sin = sin.replace(/[^0-9]/g, '');
  if (sin.length === 9 || sin.length === 0) {
    if (sin[0] === '0' || sin[0] === '8') {
      return false;
    } else {
      return luhn(sin);
    }
  } else {
    return false;
  }
}

*/

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}

export const scrollToError = (refId, inputRefs) => {
  setTimeout(() => {
    if (inputRefs.current[refId]) {
      const elementPosition = inputRefs.current[refId].getBoundingClientRect().top;
      const offset = 120;
      const finalPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({ top: finalPosition, behavior: 'smooth' });
    } else {
      console.error(`No ref found for id: ${refId}`);
    }
  }, 100);
}

export const scrollToElement = (id, inputRefs) => {
  const timer = setTimeout(() => {
    const element = inputRefs?.current?.[id] || document.getElementById(id);

    if (element) {
      const elementRect = element.getBoundingClientRect();
      const elementPosition = elementRect.top + window.scrollY;
      const offset = 300;  // Adding directly
      const finalPosition = elementPosition - offset;

      window.scrollTo({
        top: finalPosition,
        behavior: 'smooth'
      });

      element.classList.add('highlight');

      const removeHighlight = (e) => {
        if (!element.contains(e.target)) {
          element.classList.remove('highlight');
          document.removeEventListener('click', removeHighlight);
        }
      };

      // Listen for clicks outside the element to remove the highlight
      document.addEventListener('click', removeHighlight);
    } else {
      // console.log(`Failed to find element for ${id}.`);
    }
  }, 800);

  return () => clearTimeout(timer);
};

//used for insured amount
export function customRound(value) {
  const minValue = 50000;
  const maxValue = 5000000;
  const step = 5000;
  let roundedValue = Math.min(Math.max(value, minValue), maxValue);

  // Calculate the lower and upper bounds
  const lowerBound = Math.floor(roundedValue / step) * step;
  const upperBound = lowerBound + step;

  // Rounding 
  roundedValue = (roundedValue - lowerBound >= step / 2) ? upperBound : lowerBound;

  return roundedValue;
}

export const stripHtmlTags = (str) => {
  if (!str) return '';
  return str.replace(/<\/?[^>]+(>|$)/g, '');
};

export function useOutsideClick(ref, callback) {
  const handleClick = e => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  });
}

export const formatCurrency = (amount, language, isInput = false, moneyType) => {
  const showDecimals = moneyType === 'MONEY-REAL';

  let options = {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
    currencyDisplay: 'symbol',
  };

  const locale = language === 'Français' ? 'fr-FR' : 'en-CA';
  let formattedAmount = new Intl.NumberFormat(locale, options).format(amount);

  // Remove any lingering "CA" (with or without space) from the output
  formattedAmount = formattedAmount.replace(/ ?CA/, '');

  // If input and French, remove the trailing $ symbol
  if (language === 'Français' && isInput) {
    formattedAmount = formattedAmount.replace(/[\s\u202f]*\$+$/, '');
  }

  return formattedAmount;
};


/*
export const formatNumber = (amount, uiFormat, language) => {
  if (amount === null || amount === undefined || isNaN(amount)) return "";

  let options = {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0, // Default for integers
  };

  switch (uiFormat) {
      case "REAL-NUMBER":
          options = { ...options, minimumFractionDigits: 2, maximumFractionDigits: 2 };
          break;
      case "INTEGER-NUMBER":
          options = { ...options, minimumFractionDigits: 0, maximumFractionDigits: 0 };
          break;
      case "MONEY":
          options = {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          };
          break;
      default:
          return amount; // Return unformatted if no match
  }

  const locale = language === "Français" ? "fr-FR" : "en-CA";
  let formattedAmount = new Intl.NumberFormat(locale, options).format(amount);

  if (uiFormat === "MONEY") {
      formattedAmount = language === "Français" ? `${formattedAmount} $` : `$${formattedAmount}`;
  }

  return formattedAmount;
};

*/


// Block for enter
/*
useEffect(() => {
  const listener = (event) => {
    // Check if the event target is a textarea
    if (event.target.tagName !== 'TEXTAREA') {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
      }
    }
  };
  
  document.addEventListener("keydown", listener);
  return () => {
    document.removeEventListener("keydown", listener);
  };
}, []);
*/

export const formatValidationErrors = (errors = []) =>
  errors.map((error) => {
    const hasAdditionalInfo = !!error.additionalInfoDto;

    return {
      id: error.id,
      productId: error.productId,
      compositionType: error.compositionType,
      errorTitle: error.additionalInfoDto?.title || " ",
      errorContent: error.additionalInfoDto?.content || error.errorText,
      errorText: hasAdditionalInfo ? null : error.errorText,
      additionalInfoId: error.additionalInfoDto?.additionalInfoId || null,
      erroruiRenderType:
      error.additionalInfoDto?.uiRenderType || error.uiRenderType || "no",
      validationType: error.validationType || "",
      resetOnChange: error.resetOnChange === true,

    };
  });

export const isDebugMode = () => {
  return window.env?.REACT_APP_DEBUG_MODE === "true";
};

